import type {
  IDateTimeFilterAddition,
  DateTimeFilter,
  IDateRangeState,
  GTimePickerProps,
} from '~/presentation/ui';

import {
  BlockStack,
  Box,
  Button,
  ButtonGroup,
  DatePicker,
  InlineGrid,
  InlineStack,
  Popover,
  Scrollable,
  useBreakpoints,
} from '@shopify/polaris';
import {
  DateTimeFilterInputs,
  DateTimeFilters,
  useDateTimePicker,
  GTooltip,
  formatDateTimeRange,
  GButton,
  GBlockCenter,
} from '~/presentation/ui';
import { useCompareDateTimePicker } from '../hooks/useCompareDateTimePicker';
import { CalendarIcon } from '@shopify/polaris-icons';
import { dayjs, dayjsTzToDate } from '~/utils/dayjs';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface BaseTimePickerProps extends Omit<GTimePickerProps, 'isCompare'> {
  dateTimeFilters?: DateTimeFilter[];
  rangeAddition?: IDateTimeFilterAddition;
  isCompare?: boolean;
  buttonValue?: string;
}

export const BaseTimePicker: React.FC<BaseTimePickerProps> = (props) => {
  const {
    dateTimeFilters,
    rangeAddition,
    popoverProps,
    activatorProps,
    actionProps,
    isCompare,
    buttonValue,
    activator,
  } = props;

  const initialPicked = rangeAddition?.initialPicked;
  const ranges: DateTimeFilter[] = [...(dateTimeFilters || []), ...(rangeAddition?.rangers || [])];

  const useHookDateTimePicker = isCompare ? useCompareDateTimePicker : useDateTimePicker;

  const { month, year, dateRange, apply, cancel, onMonthChange, onCalendarChange, onChange } =
    useHookDateTimePicker({
      ranges,
      initialPicked,
      onApply: props.onApply,
    });

  const { t } = useTranslation();
  const { mdUp, lgUp } = useBreakpoints();

  const shouldShowMultiMonth = lgUp;
  // On tablet the presets stack above a single-month calendar, so the box spans the
  // full popover — widen it there so a timestamped value ("… at 7:19 PM") isn't
  // truncated. Mobile stays compact; desktop keeps the two-month width.
  const calendarMaxWidth = shouldShowMultiMonth ? '516px' : mdUp ? '440px' : '320px';

  const rawTooltipContent = useMemo(() => {
    // Only show tooltip when selecting Period (isVersion)
    if (!dateRange.isVersion || !dateRange.since || !dateRange.until) return;
    return formatDateTimeRange({
      since: dateRange.since,
      until: dateRange.until,
      isShowNow: dateRange.isCurrentVersion,
    });
  }, [dateRange.since, dateRange.until, dateRange.isVersion, dateRange.isCurrentVersion]);

  const [popoverActive, setPopoverActive] = useState(false);

  // Freeze tooltip content while popover is open to prevent button remount (focus/CSS loss)
  const datePickerRef = useRef(null);
  const tooltipContentRef = useRef(rawTooltipContent);

  if (!popoverActive) tooltipContentRef.current = rawTooltipContent;
  const tooltipContent = tooltipContentRef.current;

  //@ts-ignore
  function nodeContainsDescendant(rootNode, descendant) {
    if (rootNode === descendant) {
      return true;
    }
    let parent = descendant.parentNode;
    while (parent != null) {
      if (parent === rootNode) {
        return true;
      }
      parent = parent.parentNode;
    }
    return false;
  }

  //@ts-ignore
  function isNodeWithinPopover(node) {
    return datePickerRef?.current ? nodeContainsDescendant(datePickerRef.current, node) : false;
  }
  //@ts-ignore
  function handleInputBlur({ relatedTarget }) {
    const isRelatedTargetWithinPopover =
      relatedTarget != null && isNodeWithinPopover(relatedTarget);
    // If focus moves from the TextField to the Popover
    // we don't want to close the popover
    if (isRelatedTargetWithinPopover) {
      return;
    }
    setPopoverActive(false);
  }

  function applyFunc() {
    apply();
    setPopoverActive(false);
  }
  function cancelFunc() {
    cancel();
    setPopoverActive(false);
  }

  const handleTogglePopover = () => {
    if (isCompare) {
      return;
    }
    setPopoverActive(!popoverActive);
  };

  const onChangeDateTimeFilter = (data: IDateRangeState) => {
    onCalendarChange(data);
    onChange(data);
  };

  return (
    <Popover
      active={popoverActive}
      autofocusTarget="none"
      preferredAlignment="right"
      preferredPosition="below"
      fluidContent
      sectioned={false}
      fullHeight
      activator={
        <GBlockCenter height="100%" minHeight="28px" align="center">
          <GTooltip content={tooltipContent} maxWidth={activator ? 'large' : 'extra-large'}>
            <Box>
              {activator ? (
                activator({ onClick: handleTogglePopover, value: buttonValue })
              ) : (
                <Button
                  size="slim"
                  icon={!isCompare ? CalendarIcon : undefined}
                  onClick={handleTogglePopover}
                  {...activatorProps}
                >
                  {buttonValue}
                </Button>
              )}
            </Box>
          </GTooltip>
        </GBlockCenter>
      }
      onClose={cancelFunc}
      {...popoverProps}
    >
      <Scrollable vertical scrollbarWidth="thin" className="max-w-[100%]">
        <Popover.Pane fixed>
          <div ref={datePickerRef}>
            <InlineGrid
              columns={{
                xs: '1fr',
                md: 'max-content max-content',
              }}
              gap="0"
            >
              <DateTimeFilters
                setDateTimePicked={onChangeDateTimeFilter}
                dateTimePicked={dateRange}
                rangeAddition={rangeAddition}
                allRanges={ranges}
                isCompare={isCompare}
              />

              <Box
                padding="400"
                maxWidth={calendarMaxWidth}
                borderInlineStartWidth="025"
                borderColor="border-secondary"
              >
                <BlockStack gap="400">
                  <DateTimeFilterInputs
                    setDateTimePicked={onCalendarChange}
                    dateRange={dateRange}
                    onBlur={() => handleInputBlur}
                  />

                  <div>
                    <DatePicker
                      month={month}
                      year={year}
                      selected={
                        !dateRange.since && !dateRange.until
                          ? undefined
                          : {
                              start: dayjs(dateRange.since).toDate(),
                              end: dayjs(dateRange.until).toDate(),
                            }
                      }
                      onMonthChange={onMonthChange}
                      onChange={onCalendarChange}
                      multiMonth={shouldShowMultiMonth}
                      allowRange
                      disableDatesAfter={dayjsTzToDate()}
                    />
                  </div>

                  {actionProps && (
                    <GButton
                      {...actionProps}
                      onClick={() => {
                        setPopoverActive(false);
                        actionProps.onClick?.();
                      }}
                    />
                  )}
                </BlockStack>
              </Box>
            </InlineGrid>
          </div>
        </Popover.Pane>

        <Popover.Pane fixed>
          <Box padding="400" borderBlockStartWidth="025" borderColor="border-secondary">
            <InlineStack align="end" gap="200">
              <ButtonGroup>
                <Button onClick={cancelFunc}>{t('Cancel')}</Button>
                <Button variant="primary" onClick={applyFunc}>
                  {t('Apply')}
                </Button>
              </ButtonGroup>
            </InlineStack>
          </Box>
        </Popover.Pane>
      </Scrollable>
    </Popover>
  );
};
